import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";

import { Product } from "src/api/client-api/products/useGetOneProduct";
import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { ProductService } from "src/pages/Product/ProductService";
import { ProductPhotoUpload } from "src/pages/Product/components/ProductPhotoUpload";
import { ProductImagePlaceholder } from "src/pages/Product/v2/Layout/ProductImagePlaceholder";

type ProductDetailPhotoProps = {
    product: Product;
};

const normalizeLocalS3DisplayUrl = (url?: string): string | undefined => {
    if (!url) {
        return undefined;
    }

    return url.replace("https://api.foodpilot.test/mybucket/", "https://api.foodpilot.test/ui/mybucket/");
};

/**
 * Product photo on the informations detail tab (Figma `12819:62356`).
 * Upload is enabled when the user can modify the product; otherwise a read-only preview is shown.
 */
export const ProductDetailPhoto = (props: ProductDetailPhotoProps) => {
    const { product } = props;
    const theme = useTheme();
    const thumbnail = theme.custom.mediaUpload.thumbnail;
    const [formProduct, setFormProduct] = useState<ProductFormType>(() =>
        ProductService.getProductToCreate({ existingProduct: product, productReference: undefined }),
    );

    useEffect(() => {
        setFormProduct(ProductService.getProductToCreate({ existingProduct: product, productReference: undefined }));
    }, [product]);

    if (product.canModify === false) {
        const primaryPhoto = product.photos?.find((photo) => photo.isPrimary) ?? product.photos?.[0] ?? null;
        const photoUrl = normalizeLocalS3DisplayUrl(primaryPhoto?.url);

        if (!photoUrl) {
            return <ProductImagePlaceholder variant="thumbnail" />;
        }

        return (
            <Box
                sx={{
                    width: thumbnail.size,
                    height: thumbnail.size,
                    flexShrink: 0,
                    position: "relative",
                    borderRadius: theme.custom.mediaUpload.borderRadius,
                    boxShadow: thumbnail.boxShadow,
                    overflow: "hidden",
                }}
            >
                <Box sx={{ position: "absolute", inset: thumbnail.contentInset }}>
                    <Box
                        component="img"
                        src={photoUrl}
                        alt={primaryPhoto?.alt ?? primaryPhoto?.originalName ?? "Product photo"}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                </Box>
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: theme.spacing(0),
                        borderColor: thumbnail.stroke.color,
                        borderStyle: thumbnail.stroke.style,
                        borderWidth: thumbnail.stroke.width,
                        borderRadius: "inherit",
                        pointerEvents: "none",
                    }}
                />
            </Box>
        );
    }

    return (
        <ProductPhotoUpload
            product={formProduct}
            setProduct={setFormProduct as Dispatch<SetStateAction<ProductFormType>>}
            appearance="product-v2"
            variant="thumbnail"
        />
    );
};
